import { Injectable } from '@angular/core';
import { Event as RouterEvent, NavigationStart } from '@angular/router';
import { Observable, Subscription, filter } from 'rxjs';

/**
 * Overlays de Ionic que, al presentarse, ponen `aria-hidden="true"` sobre la
 * página (`ion-router-outlet#main`). El toast no la oculta, por eso no está.
 */
const HIDING_OVERLAYS = ['ION-MODAL', 'ION-ALERT', 'ION-ACTION-SHEET', 'ION-POPOVER', 'ION-LOADING', 'ION-PICKER'];

/**
 * Le quita el foco al botón que abrió un overlay antes de que Ionic oculte la
 * página.
 *
 * Ionic 7 pone el `aria-hidden` en `present()` ANTES de emitir
 * `ionModalWillPresent`, así que escuchar ese evento llega tarde: el botón
 * sigue con el foco dentro de un ancestro oculto y Chrome avisa «Blocked
 * aria-hidden on an element because its descendant retained focus». Lo que sí
 * llega antes es la inserción del overlay en el DOM (`create()`), y eso es lo
 * que se observa acá, para todos los overlays de la app en un solo lugar.
 */
@Injectable({
	providedIn: 'root',
})
export class OverlayFocusService {
	private observer?: MutationObserver;
	private navigationSub?: Subscription;

	start(root: ParentNode): void {
		this.observer?.disconnect();
		this.observer = new MutationObserver(records => {
			const overlayAdded = records.some(record =>
				Array.from(record.addedNodes).some(node => HIDING_OVERLAYS.includes(node.nodeName)),
			);

			if (overlayAdded) {
				this.releaseFocus();
			}
		});
		this.observer.observe(root, { childList: true });
	}

	stop(): void {
		this.observer?.disconnect();
		this.observer = undefined;
		this.navigationSub?.unsubscribe();
		this.navigationSub = undefined;
	}

	/**
	 * Lo mismo al cambiar de página: Ionic marca la página que se deja con
	 * `ion-page-hidden` y `aria-hidden`, y el botón que disparó la navegación
	 * seguía con el foco adentro. Se suelta al empezar la navegación.
	 */
	releaseOnNavigation(events: Observable<RouterEvent>): void {
		this.navigationSub?.unsubscribe();
		this.navigationSub = events.pipe(filter(event => event instanceof NavigationStart)).subscribe(() => this.releaseFocus());
	}

	/** Un foco que ya está dentro de otro overlay abierto no queda oculto: se conserva. */
	private releaseFocus(): void {
		const active = document.activeElement;

		if (!(active instanceof HTMLElement) || active === document.body) return;
		if (active.closest(HIDING_OVERLAYS.join(','))) return;

		active.blur();
	}
}
